import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BannerUpdate = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    order: "",
    is_active: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [imageFile, setImageFile] = useState(null); // new uploaded image
  const [preview, setPreview] = useState(null);     // preview for UI
  const [dragActive, setDragActive] = useState(false);

  // ============================================
  // Fetch Banner Detail
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.warning("Please log in first!");
      navigate("/login");
      return;
    }

    const fetchBanner = async () => {
      try {
        const res = await axios.get(ENDPOINT.BANNER_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = res.data.data; // ✔ correct backend key

        setFormData({
          name: data.name,
          order: data.order,
          is_active: data.is_active,
        });

        setPreview(`${IMAGE_BASE_URL}${data.image}`);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load banner");
      } finally {
        setLoading(false);
      }
    };

    fetchBanner();
  }, [id, navigate]);

  // ============================================
  // Input Handler
  // ============================================
  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  // ============================================
  // Image Handler
  // ============================================
  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file!");
      return;
    }
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleImageChange = (e) => handleFile(e.target.files[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // ============================================
  // UPDATE Submit
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("accessToken");

      const data = new FormData();
      data.append("name", formData.name);
      data.append("order", formData.order);
      data.append("is_active", formData.is_active);

      if (imageFile) {
        data.append("image", imageFile);
      }

      const res = await axios.put(ENDPOINT.BANNER_UPDATE(id), data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Banner updated successfully");

      setTimeout(() => navigate(`/banner/${id}`), 1200);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update banner");
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // LOADING Spinner
  // ============================================
  if (loading) {
    return (
      <Box display="flex" height="80vh" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  // ============================================
  // UI
  // ============================================
  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={600} mb={3}>
          ✏️ Update Banner
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Name */}
            <TextField
              label="Banner Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            {/* ORDER */}
            <TextField
              label="Order"
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              required
            />

            {/* ACTIVE */}
            <TextField
              select
              label="Active Status"
              name="is_active"
              value={formData.is_active}
              onChange={handleChange}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </TextField>

            {/* IMAGE UPLOAD */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("fileInput").click()}
              sx={{
                border: dragActive ? "2px dashed #1976d2" : "2px dashed #ccc",
                p: 3,
                borderRadius: 2,
                textAlign: "center",
                backgroundColor: dragActive ? "#e7f3ff" : "#fafafa",
                cursor: "pointer",
              }}
            >
              {preview ? (
                <Box>
                  <img
                    src={preview}
                    alt="Preview"
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #ddd",
                    }}
                  />
                  <Typography mt={1} variant="body2">
                    Click or Drop to replace image
                  </Typography>
                </Box>
              ) : (
                <Typography color="text.secondary">
                  Drag & Drop image here, or click to select
                </Typography>
              )}

              <input
                id="fileInput"
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Box>

            {/* BUTTONS */}
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={() => navigate(`/banner/${id}`)}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={saving}
                sx={{ px: 4 }}
              >
                {saving ? <CircularProgress size={24} /> : "Update Banner"}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default BannerUpdate;
